import React, { useEffect, useState } from 'react';
import { List, Button, Tag, Modal, message, Space, Popconfirm } from 'antd';
import { HomeOutlined, PlusOutlined, BankOutlined } from '@ant-design/icons';
import api from '../../../utils/api';
import JoinCompanyModal from './modals/JoinCompanyModal';
import RegisterCompanyModal from './modals/RegisterCompanyModal';
import CompanyDetailModal from './modals/CompanyDetailModal';
import dayjs from 'dayjs';

export default function CompanyList() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joinModalVisible, setJoinModalVisible] = useState(false);
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/me');
      if (res && res.code === 200) {
        // Fetch full details to get companies
        const userId = res.data.id;
        const fullRes = await api.get(`/users/${userId}`);
        if (fullRes && fullRes.code === 200) {
          setCompanies(fullRes.data.companies || []);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSetPrimary = async (item) => {
    if (item.isPrimary) return;

    // Check limit
    const userId = JSON.parse(localStorage.getItem('linyuan_user') || '{}').id;
    const today = dayjs().format('YYYY-MM-DD');
    const key = `primaryChangeCount_${today}_${userId}`;
    const count = parseInt(localStorage.getItem(key) || '0', 10);

    if (count >= 2) {
      message.error('您今天已达到主力企业变更次数上限（2次）');
      return;
    }

    Modal.confirm({
      title: '确认设为主力企业',
      content: `确定将 "${item.nameCn}" 设为您的主力企业吗？原主力企业将被降级。今日剩余变更次数：${2 - count}`,
      onOk: async () => {
        try {
          setLoading(true);
          // Find current primary
          const currentPrimary = companies.find(c => c.isPrimary === 1);
          
          if (currentPrimary) {
            // Downgrade current primary
            await api.put(`/user-companies/${currentPrimary.userCompanyId}`, {
              isPrimary: 0
            });
          }

          // Upgrade new primary
          const res = await api.put(`/user-companies/${item.userCompanyId}`, {
            isPrimary: 1
          });

          if (res && res.code === 200) {
            message.success('设置成功');
            localStorage.setItem(key, count + 1);
            fetchCompanies();
          } else {
            message.error(res.message || '设置失败');
          }
        } catch (e) {
          console.error(e);
          message.error('操作失败');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const showDetail = (item) => {
    setSelectedCompanyId(item.id || item.companyId); // Use companyId if enriched object id is not companyId (but usually it is)
    setDetailModalVisible(true);
  };

  return (
    <div className="profile-bottom-section">
      <div className="company-list-header">
        <h3>关联企业</h3>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setJoinModalVisible(true)}>加入企业</Button>
          <Button icon={<BankOutlined />} onClick={() => setRegisterModalVisible(true)}>登记企业</Button>
        </Space>
      </div>

      <List
        grid={{ gutter: 16, column: 3 }} // Adjust column count based on screen size via CSS if needed, or use responsive props
        dataSource={companies}
        loading={loading}
        renderItem={item => (
          <List.Item>
            <div className={`company-card ${item.isPrimary ? 'primary' : ''}`}>
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => showDetail(item)}>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{item.nameCn}</div>
                <div style={{ color: '#888', fontSize: '12px' }}>{item.nameEn || '-'}</div>
                <div style={{ marginTop: 8 }}>
                  <Tag>{item.position}</Tag>
                  {item.isPrimary === 1 && <Tag color="blue">主力企业</Tag>}
                  {item.isInPosition === 0 && <Tag color="red">已离职</Tag>}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                {item.isPrimary !== 1 && item.isInPosition === 1 && (
                  <Button size="small" type="link" onClick={() => handleSetPrimary(item)}>
                    设为主力
                  </Button>
                )}
                {/* Add Quit button if needed? LEAVE_COMPANY */}
              </div>
            </div>
          </List.Item>
        )}
      />

      <JoinCompanyModal
        visible={joinModalVisible}
        onCancel={() => setJoinModalVisible(false)}
        onSuccess={fetchCompanies} // Refresh list? Actually join is pending approval, so maybe not refresh list yet, but show message.
      />
      
      <RegisterCompanyModal
        visible={registerModalVisible}
        onCancel={() => setRegisterModalVisible(false)}
        onSuccess={fetchCompanies}
      />

      <CompanyDetailModal
        visible={detailModalVisible}
        companyId={selectedCompanyId}
        onCancel={() => setDetailModalVisible(false)}
      />
    </div>
  );
}
